import { WORSHIP_MUSIC_OPTIONS, WORSHIP_MUSIC_GROUPS, HYMNAL_ALLOWLIST_BY_TRADITION } from "../pages/parishIntelligenceOptions";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
const TIME_RE = /^\d{2}:\d{2}$/;

function Chips({ k, options, value, onToggle }) {
  const arr = Array.isArray(value) ? value : value ? [String(value)] : [];
  const legacy = arr.filter((v) => !options.includes(v));
  return (
    <div className="pp-multi" data-testid={`pf-${k}`}>
      {[...options, ...legacy].map((o) => (
        <button type="button" key={o} className={`pp-chip${arr.includes(o) ? " on" : ""}`}
          data-testid={`pf-${k}-${slug(o)}`} onClick={() => onToggle(o)}>
          {o}
        </button>
      ))}
    </div>
  );
}

function OtherInput({ k, value, onChange }) {
  return (
    <input className="pp-other-input" value={value || ""} placeholder="Please specify…"
      onChange={(e) => onChange(e.target.value)} data-testid={`pf-${k}`} />
  );
}

export default function WorshipMusicSection({ data, legacyMusic, update }) {
  const d = data || {};
  const toggle = (key) => (option) => {
    const arr = Array.isArray(d[key]) ? d[key] : d[key] ? [String(d[key])] : [];
    update(key, arr.includes(option) ? arr.filter((x) => x !== option) : [...arr, option]);
  };
  const showsOther = (f) => {
    const v = d[f.key];
    return f.otherKey && (Array.isArray(v) ? v.includes("Other") : v === "Other");
  };
  const legacyRows = Object.entries(legacyMusic || {})
    .filter(([, v]) => v != null && String(v).trim() !== "");

  const renderField = (f) => {
    const v = d[f.key];
    if (f.type === "area") {
      return (
        <div className="pp-field full" key={f.key}>
          <label>{f.label}</label>
          <textarea value={v || ""} placeholder={f.placeholder || ""} rows={5}
            onChange={(e) => update(f.key, e.target.value)} data-testid={`pf-${f.key}`} />
        </div>
      );
    }
    if (f.type === "time") {
      const isLegacy = v && !TIME_RE.test(v);
      return (
        <div className="pp-field" key={f.key}>
          <label>{f.label}{isLegacy ? " (saved value shown — pick a time to update)" : ""}</label>
          {isLegacy
            ? <input value={v} onChange={(e) => update(f.key, e.target.value)} data-testid={`pf-${f.key}`} />
            : <input type="time" value={v || ""} onChange={(e) => update(f.key, e.target.value)} data-testid={`pf-${f.key}`} />}
        </div>
      );
    }
    if (f.type === "select") {
      let options = WORSHIP_MUSIC_OPTIONS[f.key] || [];
      // Governance rule (owner directive): primary_hymnal_source_id is
      // scoped by the parish's primary_worship_tradition. H1982 is
      // Episcopal only; LCH is Roman Catholic only. Filter the option
      // list against the tradition-scoped allowlist so a parish cannot
      // even see a hymnal that isn't valid for its tradition.
      if (f.key === "primary_hymnal_source_id") {
        const tradition = d.primary_worship_tradition || "";
        const allowlist = HYMNAL_ALLOWLIST_BY_TRADITION[tradition] || null;
        if (allowlist) {
          options = options.filter((o) => allowlist.includes(o.value));
        }
      }
      // Support both plain strings and {value,label} objects (Primary
      // Hymnal picker uses the latter so the stable source_id is stored,
      // not the display label).
      const norm = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
      const legacyVal = v && !norm.some((o) => o.value === v) ? v : null;
      // For fields flagged noCeilingOption (e.g. organ_difficulty_ceiling),
      // the empty selection is a first-class option labelled
      // "No ceiling (Recommended)". The backend recommender treats an
      // empty string as uncapped.
      const emptyLabel = f.noCeilingOption ? "No ceiling (Recommended)" : "—";
      return (
        <div className="pp-field" key={f.key}>
          <label>{f.label}</label>
          <select value={v || ""} onChange={(e) => update(f.key, e.target.value)} data-testid={`pf-${f.key}`}>
            <option value="">{emptyLabel}</option>
            {norm.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            {legacyVal && <option value={legacyVal}>{legacyVal} (saved)</option>}
          </select>
          {f.helper && <div className="pp-help" style={{ fontSize: 12, color: "#6f6a5a", marginTop: 4 }}>{f.helper}</div>}
          {f.key === "primary_hymnal_source_id" && norm.length === 0 && (
            <div className="pp-help" data-testid="pf-primary_hymnal_source_id-empty"
                 style={{ fontSize: 12, color: "#8a5a1a", marginTop: 4 }}>
              No verified hymnal crosswalk is available yet for this tradition. Recommendations will be sourced from your Hymnal Resources list below.
            </div>
          )}
          {showsOther(f) && <OtherInput k={f.otherKey} value={d[f.otherKey]} onChange={(x) => update(f.otherKey, x)} />}
        </div>
      );
    }
    if (f.type === "grouped_multi") {
      const groups = WORSHIP_MUSIC_OPTIONS[f.key] || {};
      const all = Object.values(groups).flat();
      const arr = Array.isArray(v) ? v : v ? [String(v)] : [];
      const legacy = arr.filter((x) => !all.includes(x));
      return (
        <div className="pp-field full" key={f.key}>
          <label>{f.label}</label>
          <div data-testid={`pf-${f.key}`}>
            {Object.entries(groups).map(([group, opts]) => (
              <div className="pp-optgroup" key={group}>
                <span className="pp-optgroup-label">{group}</span>
                <Chips k={f.key} options={opts} value={arr} onToggle={toggle(f.key)} />
              </div>
            ))}
            {legacy.length > 0 && (
              <div className="pp-optgroup">
                <span className="pp-optgroup-label">Saved from your earlier profile</span>
                <Chips k={f.key} options={[]} value={legacy} onToggle={toggle(f.key)} />
              </div>
            )}
          </div>
          {showsOther(f) && <OtherInput k={f.otherKey} value={d[f.otherKey]} onChange={(x) => update(f.otherKey, x)} />}
        </div>
      );
    }
    // multi
    return (
      <div className="pp-field full" key={f.key}>
        <label>{f.label}</label>
        <Chips k={f.key} options={WORSHIP_MUSIC_OPTIONS[f.key] || []} value={v} onToggle={toggle(f.key)} />
        {showsOther(f) && <OtherInput k={f.otherKey} value={d[f.otherKey]} onChange={(x) => update(f.otherKey, x)} />}
      </div>
    );
  };

  return (
    <div className="pp-wm" data-testid="worship-music-section">
      {WORSHIP_MUSIC_GROUPS.map((g) => (
        <div className="pp-wm-group" key={g.title} data-testid={`wm-group-${slug(g.title)}`}>
          <h3 className="pp-wm-title">{g.title}</h3>
          <div className="pp-grid">{g.fields.map(renderField)}</div>
        </div>
      ))}
      {legacyRows.length > 0 && (
        <div className="pp-wm-group pp-wm-legacy" data-testid="wm-legacy-panel">
          <h3 className="pp-wm-title">Earlier Music Notes (preserved)</h3>
          <p className="pp-wm-legacy-note">These values from your original Music tab are kept and still inform Ordo AI™.</p>
          <ul>
            {legacyRows.map(([k, v]) => (
              <li key={k}><b>{k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}:</b> {String(v)}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
